import { Button, displayThousands, Space } from '@bfe/moly';
import { EyeoffIcon, EyeonIcon } from '@bfe/moly-icons';
import { Badge, Card } from 'antd';
import classNames from 'classnames';
import { multiply } from 'lodash-es';
import { Dispatch, FC, SetStateAction, useState } from 'react';

import ApiAuthModal, { ApiAuthScene } from '@/components/ApiAuthModal';
import useEscrowAccount from '@/hooks/useEscrowAccount';
import { useI18nContext } from '@/i18n/I18n';
import { useUserStore } from '@/store/useUser';
import { complianceHide } from '@/utils/complianceHelpers';
import { accountType } from '@/utils/pageConfig/subAccount';

import styles from '../../index.module.less';
import SelectSubAccountTypeModal, {
  showCreateModal,
} from '../CreateSubAccount';
import { useTotalBalance } from './useTotalBalance';

// 隐藏时候显示8个*
const hidePunctuation = '********';

interface ITotalBalance {
  profile: any;
  currencyCode: string;
  refreshTable(type?: string, action?: any, page?: number): void;
  setSwitchListChecked: Dispatch<SetStateAction<boolean>>;
  msgError(error: any): void;
  exchangeRate: string;
  onTransfer: () => void;
  isExchangeTeam: boolean;
  bindRequestCount: number;
  submemberList: any[];
  totalBalance: string | number;
  contractTotalBalance: string;
}

export const TotalBalance: FC<ITotalBalance> = ({
  profile,
  currencyCode,
  refreshTable,
  setSwitchListChecked,
  msgError,
  exchangeRate,
  onTransfer,
  isExchangeTeam,
  bindRequestCount,
  submemberList,
  totalBalance,
  contractTotalBalance,
}) => {
  const [$t, language] = useI18nContext();
  const { accountTypeName, authName, needSubApiAuth, updateNeedSubApiAuth } =
    useEscrowAccount();
  const [visibleAuthModal, setVisibleAuthModal] = useState(false);

  const { complianceConfig } = useUserStore();

  const { isHide, visible, toggle, setVisible, toBindRequest } =
    useTotalBalance();

  return (
    <>
      <Card className="bybit-subAccount-box" bordered={false}>
        <div
          className="bybit-subAccount-box-header"
          style={{ display: 'flex', flexDirection: 'row' }}
        >
          <div
            className="bybit-subAccount-box-header-title"
            style={{ width: '200px' }}
          >
            <span>{$t('child.accountNew')}</span>
          </div>
          <div style={{ flex: 1, textAlign: 'right' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '16px',
              }}
            >
              <Button
                variant="secondary"
                size="x-small"
                data-size="x-small"
                onClick={() =>
                  window.open('/user/assets/subAccountTransferRecord')
                }
              >
                {$t('child.transferRecord')}
              </Button>

              {submemberList.length > 0 && (
                <Button
                  variant="secondary"
                  size="x-small"
                  data-size="x-small"
                  onClick={onTransfer}
                >
                  {$t('child.fundsTransfer')}
                </Button>
              )}

              <Button
                variant="primary"
                size="x-small"
                onClick={() => {
                  if (needSubApiAuth) {
                    setVisibleAuthModal(true);
                  } else {
                    setVisible(true);
                  }
                }}
              >
                {$t('child.createASubaccount')}
              </Button>
              {isExchangeTeam &&
                !complianceHide(
                  complianceConfig?.TrustSubAccountCreateEntry,
                ) && (
                  <Button
                    variant="secondary"
                    size="x-small"
                    data-size="x-small"
                    onClick={toBindRequest}
                    className="bindRequest-btn"
                  >
                    {bindRequestCount > 0 ? (
                      <Badge dot>
                        <span className="bindRequest-btn-span">
                          {$t('bindRequest')}
                        </span>
                      </Badge>
                    ) : (
                      <span>{$t('bindRequest')}</span>
                    )}
                  </Button>
                )}
            </div>
          </div>
        </div>

        <div className={styles.totalAsset}>
          <Space size={4}>
            <span>{$t('Total Equity')}</span>
            <span className={styles.totalAssetEye} onClick={toggle}>
              {isHide ? <EyeoffIcon /> : <EyeonIcon />}
            </span>
          </Space>
        </div>

        <div className={styles.assetLeft}>
          <span
            className={classNames(
              styles.assetBtcSum,
              'text-[24px] font-semibold',
            )}
          >
            {isHide
              ? hidePunctuation
              : totalBalance !== '--'
              ? displayThousands(
                  multiply(Number(totalBalance), Number(exchangeRate) || 0),
                  8,
                  language,
                )
              : '--'}
          </span>
          <span className="text-[14px] font-normal">{currencyCode}</span>
        </div>
        <div className={styles.assetUsd}>
          <span>≈ </span>
          <span>
            {isHide
              ? hidePunctuation
              : contractTotalBalance !== '--'
              ? displayThousands(contractTotalBalance, 8, language)
              : '--'}
          </span>
          <span className={styles.coin}> BTC</span>
        </div>
      </Card>

      <SelectSubAccountTypeModal
        visible={visible}
        setVisible={setVisible}
        user={profile}
        updateList={() => {
          refreshTable(undefined, undefined, 1);
        }}
        setSwitchListChecked={setSwitchListChecked}
        directly={needSubApiAuth}
      />

      {visibleAuthModal && (
        <ApiAuthModal
          to={authName}
          from={ApiAuthScene['subAccount']}
          text={$t('apiAuthModal.copper.subAccountTextNew').replaceAll(
            '{{name}}',
            $t(accountTypeName),
          )}
          accountTypeName={accountTypeName}
          onSuccess={() => {
            updateNeedSubApiAuth(false);
            setVisibleAuthModal(false);
            showCreateModal({
              visible: true,
              type: accountType.COMMON,
              userInfo: profile?.result,
            });
          }}
          onCancel={() => {
            setVisibleAuthModal(false);
          }}
        />
      )}
    </>
  );
};
